import { ChevronDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LANGUAGES from './LANGUAGES';
import { Checkbox } from '@/Domain/components/Shared/CheckBox/Checkbox';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Domain/components/Shared/Popover/Popover';
import { selectTriggerVariant } from '@/Domain/components/Shared/Select/Select';
import { cn } from '@/Domain/utils/cn';

type LanguageCode = (typeof LANGUAGES)[number];

type Props = {
  value: LanguageCode[];
  onChange: (next: LanguageCode[]) => void;
  limit?: number;
  error?: boolean;
  id?: string;
  /**
   * Appended last to the trigger's classes, so a caller can restyle it without an
   * arbitrary-variant selector reaching in from outside. Undefined leaves the
   * emitted class string exactly as it was, which is what the Identity page needs.
   * Same escape hatch `AddLanguagePopover` and `PromptTemplateDropdown` expose.
   */
  triggerClassName?: string;
  'data-testid'?: string;
};

export default function LanguageMultiSelect({
  value,
  onChange,
  limit,
  error,
  id,
  triggerClassName,
  'data-testid': testId = 'language-multi-select',
}: Props) {
  const { t } = useTranslation(['settings', 'common']);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const nameOf = (code: LanguageCode) => t(`languages.${code}`, { ns: 'common' });

  const options = useMemo(
    () =>
      LANGUAGES.map((code) => ({ value: code, label: nameOf(code) })).sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const atLimit = limit !== undefined && value.length >= limit;

  const toggle = (code: LanguageCode) => {
    if (value.includes(code)) {
      if (value.length === 1) return; // always keep the primary language
      onChange(value.filter((c) => c !== code));
    } else {
      if (atLimit) return;
      onChange([...value, code]);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          data-testid={testId}
          className={cn(
            selectTriggerVariant({ variant: 'bordered' }),
            'w-full',
            { 'border-destructive': error },
            triggerClassName,
          )}
        >
          <span className="!block min-w-0 truncate">{value.map(nameOf).join(', ')}</span>
          <ChevronDown className="size-4 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[var(--radix-popover-trigger-width)] p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b border-border p-2">
          <Input
            size="sm"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('search', { ns: 'common', defaultValue: 'Search...' })}
            startIcon={<Search className="size-4 text-foreground-subtle" />}
            autoFocus
            aria-label={t('search', { ns: 'common', defaultValue: 'Search' })}
          />
        </div>

        <div className="max-h-64 overflow-auto p-1" role="listbox" aria-multiselectable="true">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-foreground-subtle">
              {t('no_results', { ns: 'common', defaultValue: 'No results' })}
            </div>
          ) : (
            filtered.map((option) => {
              const checked = value.includes(option.value);
              const isPrimary = value[0] === option.value;
              const disabled = !checked && atLimit;
              return (
                <button
                  type="button"
                  key={option.value}
                  role="option"
                  aria-selected={checked}
                  disabled={disabled}
                  onClick={() => toggle(option.value)}
                  data-testid={`language-option-${option.value}`}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm text-foreground outline-none hover:bg-accent focus:bg-accent',
                    'disabled:pointer-events-none disabled:opacity-50',
                  )}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(option.value)}
                    tabIndex={-1}
                    className="pointer-events-none"
                  />
                  <span className="min-w-0 flex-1 truncate font-medium">{option.label}</span>
                  {isPrimary && (
                    <span className="shrink-0 text-xs font-medium text-foreground-subtle">
                      {t('primary', { ns: 'common' })}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>

        {limit !== undefined && (
          <div className="flex justify-end border-t border-border px-3 py-2 text-xs text-foreground-subtle">
            {value.length} / {limit}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
